/**
 * <collection-list-slider>
 *
 * Extends the theme's base SliderComponent with the one piece it does not ship
 * with: a seamless continuous loop. Scrolling itself is left entirely to the
 * browser's native smooth scrolling, same as every other slider in the theme.
 *
 * The loop works by appending clones of the slides until the track is at least
 * twice the width of the viewport. Scrolling one full set of slides lands on a
 * pixel-identical view, so the scroll position can be silently rewound by one set
 * once the scroll settles -- the seam is never visible.
 *
 * Data attributes read from the element:
 * - data-loop="true|false"
 * - data-autoplay="true|false"
 * - data-autoplay-speed="<seconds>"
 */
customElements.whenDefined('slider-component').then(() => {
  if (customElements.get('collection-list-slider')) return;

  const SliderComponent = customElements.get('slider-component');
  const SETTLE_DELAY = 150;
  const MAX_CLONE_SETS = 4;

  class CollectionListSlider extends SliderComponent {
    constructor() {
      super();

      if (!this.slider || !this.nextButton) return;

      this.loopEnabled = this.dataset.loop === 'true';
      if (this.loopEnabled) this.setUpLoop();

      this.adoptDuplicateButtons();

      this.slider.addEventListener('scroll', this.onScroll.bind(this), { passive: true });

      if (this.dataset.autoplay === 'true') this.initAutoplay();
    }

    disconnectedCallback() {
      this.stopAutoplay();
      clearTimeout(this.settleTimeout);
    }

    initPages() {
      super.initPages();
      // Runs on every resize, so this is where the clones follow the breakpoint and the
      // loop period gets re-measured against the new slide offsets.
      if (this.loopEnabled) this.syncClones();
    }

    update() {
      super.update();
      this.syncDuplicateButtons();
    }

    /* Duplicate controls ----------------------------------------------- */

    /**
     * The arrows are rendered twice -- once for the header/overlay and once for the
     * phone row beneath the cards -- but SliderComponent only ever binds the first
     * pair it finds. Everything past that pair is wired up and kept in step here.
     */
    adoptDuplicateButtons() {
      const buttons = this.querySelectorAll('.collection-list__nav button[name="previous"], .collection-list__nav button[name="next"]');

      this.duplicateButtons = Array.from(buttons).filter(
        (button) => button !== this.prevButton && button !== this.nextButton
      );
      this.duplicateButtons.forEach((button) => button.addEventListener('click', this.onButtonClick.bind(this)));
      this.syncDuplicateButtons();
    }

    syncDuplicateButtons() {
      if (!this.duplicateButtons || !this.duplicateButtons.length) return;

      this.duplicateButtons.forEach((button) => {
        const source = button.name === 'next' ? this.nextButton : this.prevButton;

        if (source.hasAttribute('disabled')) {
          button.setAttribute('disabled', 'disabled');
        } else {
          button.removeAttribute('disabled');
        }
      });
    }

    /* Looping ---------------------------------------------------------- */

    setUpLoop() {
      this.originalSlides = Array.from(this.slider.querySelectorAll('[id^="Slide-"]'));

      if (this.originalSlides.length < 2) {
        this.loopEnabled = false;
        return;
      }

      // Stops the inherited update() from disabling the arrows at either end.
      this.enableSliderLooping = true;
      this.prevButton.removeAttribute('disabled');
      this.nextButton.removeAttribute('disabled');

      this.syncClones();
    }

    /**
     * The track is only a scroller on the breakpoints the merchant switched the slider
     * on for; everywhere else it lays out as a plain grid. Clones left in the markup
     * would show up there as duplicate collections, so they only exist while the track
     * is actually scrollable.
     */
    isSliderActive() {
      const overflowX = getComputedStyle(this.slider).overflowX;

      return overflowX === 'auto' || overflowX === 'scroll';
    }

    syncClones() {
      const hasClones = this.slider.querySelector('[data-slider-clone]') !== null;
      const shouldHaveClones = this.isSliderActive();

      if (shouldHaveClones && !hasClones) {
        this.buildClones();
        return;
      }

      if (!shouldHaveClones && hasClones) {
        this.removeClones();
        return;
      }

      if (hasClones) this.loopWidth = this.measureLoopWidth();
    }

    removeClones() {
      this.slider.querySelectorAll('[data-slider-clone]').forEach((clone) => clone.remove());
      this.loopWidth = 0;
    }

    buildClones() {
      this.slider.querySelectorAll('[data-slider-clone]').forEach((clone) => clone.remove());

      let sets = 0;
      do {
        const fragment = document.createDocumentFragment();
        this.originalSlides.forEach((slide) => fragment.appendChild(this.buildClone(slide)));
        this.slider.appendChild(fragment);
        sets += 1;
      } while (sets < MAX_CLONE_SETS && this.slider.scrollWidth < this.slider.clientWidth * 2);

      this.loopWidth = this.measureLoopWidth();
    }

    buildClone(slide) {
      const clone = slide.cloneNode(true);

      clone.removeAttribute('id');
      clone.removeAttribute('data-cascade');
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('data-slider-clone', '');

      // Block ids would confuse the theme editor, and the reveal-on-scroll observer
      // never sees these nodes, so anything it would have unhidden stays at opacity 0.
      Array.from(clone.attributes).forEach((attribute) => {
        if (attribute.name.startsWith('data-shopify')) clone.removeAttribute(attribute.name);
      });
      clone.classList.remove('scroll-trigger', 'animate--slide-in');
      clone.querySelectorAll('.scroll-trigger').forEach((element) => {
        element.classList.remove('scroll-trigger', 'animate--slide-in');
      });
      clone.querySelectorAll('a, button, [tabindex]').forEach((element) => {
        element.setAttribute('tabindex', '-1');
      });

      return clone;
    }

    measureLoopWidth() {
      const firstClone = this.slider.querySelector('[data-slider-clone]');
      if (!firstClone || !this.originalSlides || !this.originalSlides.length) return 0;

      return firstClone.offsetLeft - this.originalSlides[0].offsetLeft;
    }

    onScroll() {
      if (!this.loopEnabled) return;

      // Debounced, so this only lands once the scroll -- native smooth or a finger
      // swipe -- has actually come to rest.
      clearTimeout(this.settleTimeout);
      this.settleTimeout = setTimeout(this.normalisePosition.bind(this), SETTLE_DELAY);
    }

    /**
     * Rewinds by one full set once the scroll has come to rest inside the clones.
     * The view either side of the seam is identical, so nothing moves on screen.
     */
    normalisePosition() {
      if (!this.loopEnabled || !this.loopWidth) return;
      if (this.slider.scrollLeft < this.loopWidth) return;

      this.jumpBy(-this.loopWidth);
    }

    jumpBy(delta) {
      const slider = this.slider;
      const snapType = slider.style.scrollSnapType;
      const behavior = slider.style.scrollBehavior;

      slider.style.scrollSnapType = 'none';
      slider.style.scrollBehavior = 'auto';
      slider.scrollLeft += delta;
      slider.style.scrollBehavior = behavior;
      slider.style.scrollSnapType = snapType;
    }

    /* Scrolling -------------------------------------------------------- */

    setSlidePosition(position) {
      let target = position;

      if (this.loopEnabled && this.loopWidth && target < 0) {
        // Jump forward one identical-looking set so there is room to scroll back into.
        this.jumpBy(this.loopWidth);
        target += this.loopWidth;
      }

      super.setSlidePosition(target);
    }

    /* Autoplay --------------------------------------------------------- */

    initAutoplay() {
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.autoplaySpeed = (parseFloat(this.dataset.autoplaySpeed) || 5) * 1000;
      this.autoplayTimer = null;

      ['mouseenter', 'focusin', 'touchstart'].forEach((event) =>
        this.addEventListener(event, this.stopAutoplay.bind(this), { passive: true })
      );
      ['mouseleave', 'focusout'].forEach((event) => this.addEventListener(event, this.startAutoplay.bind(this)));

      this.reducedMotion.addEventListener('change', () =>
        this.reducedMotion.matches ? this.stopAutoplay() : this.startAutoplay()
      );

      // Only run while the section is actually on screen.
      this.autoplayObserver = new IntersectionObserver((entries) => {
        entries[0].isIntersecting ? this.startAutoplay() : this.stopAutoplay();
      });
      this.autoplayObserver.observe(this);
    }

    startAutoplay() {
      if (!this.autoplaySpeed || this.reducedMotion.matches) return;

      this.stopAutoplay();
      this.autoplayTimer = setInterval(this.advance.bind(this), this.autoplaySpeed);
    }

    stopAutoplay() {
      if (!this.autoplayTimer) return;

      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    advance() {
      if (!this.sliderItemOffset) return;

      const next = this.slider.scrollLeft + this.sliderItemOffset;

      if (this.loopEnabled) {
        this.setSlidePosition(next);
        return;
      }

      const atEnd = this.slider.scrollLeft + this.slider.clientWidth >= this.slider.scrollWidth - 1;
      this.setSlidePosition(atEnd ? 0 : next);
    }
  }

  customElements.define('collection-list-slider', CollectionListSlider);
});
